/**
 * Structured API failure.
 *
 * Every failure that leaves this module is an `ApiError`. Components never see a
 * raw `Response`, a `ZodError`, or a bare `Error` from the transport — they can
 * switch on `code` and render an appropriate state.
 */
import { ApiErrorEnvelopeSchema, ProviderErrorSchema } from '@/api/schemas/common';
import type { ErrorDetail } from '@/api/schemas/common';

/**
 * Raised when a response body does not match its schema.
 *
 * This is the mechanism the design spec depends on. Without it, contract drift
 * surfaces as a screen rendering `undefined` where a name should be. With it,
 * drift surfaces as ONE loud, attributable failure at a known location, naming
 * the endpoint, the request id and the offending paths.
 *
 * The intent is that this class of failure is noticed in development and in
 * tests, long before a user sees it.
 */
export class ContractMismatchError extends Error {
  readonly status: number;
  readonly requestId: string | undefined;
  readonly endpoint: string;
  readonly issues: ReadonlyArray<{ path: string; message: string }>;

  constructor(init: {
    endpoint: string;
    status: number;
    requestId: string | undefined;
    issues: ReadonlyArray<{ path: string; message: string }>;
  }) {
    const sample = init.issues
      .slice(0, 3)
      .map((i) => `${i.path || '(root)'}: ${i.message}`)
      .join('; ');

    super(
      `Response from ${init.endpoint} did not match its contract. ` +
        `${init.issues.length} issue(s): ${sample}`,
    );
    this.name = 'ContractMismatchError';
    this.endpoint = init.endpoint;
    this.status = init.status;
    this.requestId = init.requestId;
    this.issues = init.issues;
  }
}

export type ApiProblemInit = {
  status: number;
  code: string;
  message: string;
  requestId?: string;
  details?: readonly ErrorDetail[];
};

/** A failure the server described. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  /**
   * The server's field-level details, as sent: `[{ path, code }]`.
   *
   * Kept in the wire shape rather than folded into a map. A caller that wants
   * "which field failed" reads `details[].path`; a caller that wants the
   * machine token reads `details[].code`. Collapsing them here would lose the
   * pairing between the two, which is the only thing that makes a detail
   * actionable.
   */
  readonly details: readonly ErrorDetail[];

  constructor(init: ApiProblemInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.details = init.details ?? [];
  }

  /**
   * The field paths the server named, in order, without duplicates.
   *
   * A convenience over `details` for the common case of highlighting the
   * offending inputs. Returns an empty array when the server named no field —
   * never a guess.
   */
  get fieldPaths(): readonly string[] {
    return [...new Set(this.details.map((detail) => detail.path))];
  }

  /** True when the failure is about this client's request, not the server's state. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * True when retrying the identical request could plausibly succeed.
   *
   * Deliberately conservative: a 5xx or a rate limit MAY be retried, anything
   * else may not. Never infer retryability from a network error alone — a POST
   * that may have succeeded server-side must not be replayed blindly.
   */
  get isRetryable(): boolean {
    return this.code === 'RATE_LIMITED' || this.status >= 500;
  }
}

/**
 * Converts a non-2xx response body into an `ApiError`.
 *
 * Tolerates a malformed body: an unparseable error response is still an error,
 * and reporting "the server returned 502 with an unreadable body" is more useful
 * — and safer — than discarding the status and reporting a parse failure.
 *
 * TWO WIRE SHAPES ARE RECOGNISED
 * -------------------------------
 * The shared envelope `{ error: { code, message } }` is the platform's own. The
 * authentication provider's routes answer `{ message, code }` at the top level
 * instead — they are the provider's routes, not platform operations.
 *
 * The provider's shape is read rather than discarded because its CODE is
 * load-bearing: `INVALID_ORIGIN` and `EMAIL_NOT_VERIFIED` share a 403, so a
 * caller left with only the status cannot tell "this console's address is not
 * trusted" from "this identity is not verified" — and will say the wrong one.
 */
export function toApiProblem(status: number, payload: unknown): ApiError {
  const parsed = ApiErrorEnvelopeSchema.safeParse(payload);

  if (!parsed.success) {
    const provider = ProviderErrorSchema.safeParse(payload);
    if (provider.success) {
      return new ApiError({
        status,
        code: provider.data.code,
        message: provider.data.message,
      });
    }

    return new ApiError({
      status,
      code: 'UNEXPECTED_ERROR_SHAPE',
      message: `Request failed with status ${status} and an unreadable error body.`,
    });
  }

  return new ApiError({
    status,
    code: parsed.data.error.code,
    message: parsed.data.error.message,
    ...(parsed.data.error.requestId ? { requestId: parsed.data.error.requestId } : {}),
    ...(parsed.data.error.details ? { details: parsed.data.error.details } : {}),
  });
}